<template>
  <div class="route-map">
    <GmapMap
      ref="map"
      :center="mapCenter"
      :zoom="15"
      map-type-id="roadmap"
      :options="mapOptions"
      class="map-canvas"
    >
      <GmapMarker
        v-for="location in parsedLocations"
        :key="location.number"
        :position="location.position"
        :clickable="true"
        :title="location.title"
        :label="{
          text: String(location.number),
          color: '#14342B',
          fontSize: '13px',
          fontWeight: '800',
        }"
        :icon="{
          url: location.canCall ? require('../assets/green-circle.png') : require('../assets/yellow-circle.png'),
        }"
        @click="openInfoWindow(location.number)"
      >
        <GmapInfoWindow :opened="openNumber === location.number" @closeclick="openNumber = null">
          <div class="info-window">
            <p class="info-title">{{ location.number }}. {{ location.title }}</p>
            <span v-if="location.canCall" class="info-badge">{{ $t("ticket.route.pickupPoint") }}</span>
            <p v-if="location.description" class="info-text">{{ location.description }}</p>
            <a :href="location.direction" target="_blank" rel="noopener" class="info-link">
              {{ $t("ticket.route.directions") }} →
            </a>
          </div>
        </GmapInfoWindow>
      </GmapMarker>

      <!-- Line joining the stops in route order -->
      <GmapPolyline
        v-if="parsedLocations.length > 1"
        :path="parsedLocations.map((loc) => loc.position)"
        :options="{ strokeColor: '#2C6E57', strokeOpacity: 0.9, strokeWeight: 4 }"
      />
    </GmapMap>

    <ul class="legend">
      <li>
        <img src="../assets/green-circle.png" alt="" class="legend-icon" />
        {{ $t("ticket.route.pickupPoint") }}
      </li>
      <li>
        <img src="../assets/yellow-circle.png" alt="" class="legend-icon" />
        {{ $t("ticket.route.stopLabel") }}
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "RouteMapView",
  props: {
    locations: { type: Array, default: () => [] },
  },
  data() {
    return {
      mapCenter: { lat: 38.71373554417725, lng: -9.13447565446782 }, // Lisbon, until the route is fitted
      openNumber: null, // which stop's info window is open
      mapOptions: {
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
        clickableIcons: false,
        gestureHandling: "cooperative", // two fingers to move on phones, so the page still scrolls
      },
    };
  },
  computed: {
    // Stops with coordinates; `number` is the stop's place on the route
    parsedLocations() {
      return this.locations
        .map((stop, i) => {
          const position = this.extractLatLngFromUrl(stop.direction);
          return position ? { ...stop, number: stop.number || i + 1, position } : null;
        })
        .filter(Boolean);
    },
  },
  mounted() {
    this.fitRoute();
  },
  watch: {
    parsedLocations() {
      this.fitRoute();
    },
  },
  methods: {
    // "https://www.google.com/maps/.../@38.71,-9.13,17z" -> { lat, lng }
    extractLatLngFromUrl(direction) {
      if (!direction || typeof direction !== "string") return null;
      const match = direction.match(/@(-?[0-9.]+),(-?[0-9.]+)/);
      return match ? { lat: parseFloat(match[1]), lng: parseFloat(match[2]) } : null;
    },
    // Zoom so every stop is on screen
    fitRoute() {
      if (!this.$refs.map || this.parsedLocations.length < 2) return;
      this.$refs.map.$mapPromise.then((map) => {
        const bounds = new window.google.maps.LatLngBounds();
        this.parsedLocations.forEach((loc) => bounds.extend(loc.position));
        map.fitBounds(bounds, 40);
      });
    },
    openInfoWindow(number) {
      this.openNumber = number;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

.map-canvas {
  width: 100%;
  height: 28rem;
  border-radius: $radius-large;
  overflow: hidden;
  border: 1px solid $line;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  margin-top: 0.875rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: $forest;

  li {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
}

.legend-icon {
  height: 1.25rem;
  width: auto;
}

.info-window {
  max-width: 15rem;
  font-family: $family-primary;
  color: $forest;
}

.info-title {
  font-weight: 800;
}

.info-badge {
  display: inline-block;
  margin-top: 0.375rem;
  padding: 0.1rem 0.5rem;
  border-radius: $radius-rounded;
  background: $lime;
  font-size: 0.75rem;
  font-weight: 800;
}

.info-text {
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: $ink-sub;
}

.info-link {
  display: inline-block;
  margin-top: 0.5rem;
  font-weight: 700;
  color: $forest-2;
}

@include mobile {
  .map-canvas {
    height: 22rem;
  }
}
</style>
